import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/first';

import { Injectable, OnDestroy } from '@angular/core';

import { AngularFireAuth } from 'angularfire2/auth';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';

import { Guest } from '../models/guest-ao';
import { DatabaseService } from './database.service';
import { RedirectingService } from './redirecting.service';
import { doCondition } from '../misc/observable-case';
import { LanguagesEnum } from './language.service';

@Injectable()
export class GuestService implements OnDestroy {
  private _guestSubject = new BehaviorSubject(null);
  // private _guestSubject: Subject<Guest>;
  private _currentGuest: Guest = null;
  private _guestSubscription: Subscription;
  private _redirSubscription: Subscription;

  public guest$: Observable<Guest>;
  public name$: Observable<{ name: string }>;

  constructor(
    private db: DatabaseService,
    private redirect: RedirectingService,
    private afa: AngularFireAuth,
  ) {
    this.guest$ = this._guestSubject.shareReplay(1);
    this.name$ = this._guestSubject
      .map(
        guest => (guest == null ? { name: 'mmm.....' } : { name: guest.name }),
      )
      .shareReplay(1);
    this._setupGuestBuilderOnUserChange();
    this._setupRedirectionOnGuestArrival();
  }

  ngOnDestroy() {
    if (this._guestSubscription) {
      this._guestSubscription.unsubscribe();
    }
    if (this._redirSubscription) {
      this._redirSubscription.unsubscribe();
    }
  }

  private _setupRedirectionOnGuestArrival() {
    this._redirSubscription = this.guest$
      // .do(guest => console.log('guest action - guest changed:', guest))
      .doCondition(
        guest => guest != null,
        guest =>
          this.redirect.toRestrictedDomain(
            `GuestService guest change: ${guest.id} ${guest.coming}`,
          ),
      )
      .subscribe();
  }

  private _setupGuestBuilderOnUserChange() {
    this._guestSubscription = this.afa.authState
      .switchMap(
        user => (user == null ? Observable.of(null) : this.getGuest(user.uid)),
      )
      .do(guest => (this._currentGuest = guest))
      .subscribe(this._guestSubject);
  }

  private sanitizeGuestData(guestData: Partial<Guest>) {
    const guest = Object.assign({}, guestData);
    delete guest.id;
    return guest;
  }

  private buildDbRef(userId: string) {
    return `guests/${userId}`;
  }

  private injectUserId(userId: string) {
    return (guest: Guest) => {
      return guest == null ? null : { id: userId, ...guest };
    };
  }

  private get currentGuestId() {
    const currentGuest = this._currentGuest;
    return currentGuest == null ? null : currentGuest.id;
  }

  public getGuest(userId?: string): Promise<Guest> {
    const _userId = userId || this.currentGuestId;

    if (_userId == null) {
      return Promise.reject('UserId cannot be null');
    }

    return this.db
      .getDoc<Guest>(this.buildDbRef(userId || this.currentGuestId))
      .first()
      .map(this.injectUserId(userId))
      .toPromise();
  }

  public updateGuest(userId: string, guest: Partial<Guest>): Promise<void> {
    const _guest = this.sanitizeGuestData(guest);
    // this._guestSubject.next(_guest);
    this._currentGuest = $.extend(this._currentGuest, guest);
    return this.db.updateDoc(this.buildDbRef(userId), _guest);
  }

  public updateComing(
    coming: boolean | undefined | null,
    userId?: string,
  ): Promise<void> {
    const guestId = userId == null ? this.currentGuestId : userId;
    if (guestId == null) {
      return Promise.reject(`GuestId is null ${this.currentGuestId} ${userId}`);
    }
    const guest: Partial<Guest> = {
      coming: coming == null ? this.db.delete : coming,
    };
    return this.updateGuest(guestId, guest);

    // return this.db.updateDoc(this.buildDbRef(guestId), guest);
  }

  public updateLanguage(language: string, userId?: string) {
    const guestId = userId == null ? this.currentGuestId : userId;
    if (guestId == null) {
      return;
    }
    const guest = {
      lang: language == null ? this.db.delete : language,
    };

    return this.db.updateDoc(this.buildDbRef(guestId), guest);
  }

  public getLanguage(): Promise<LanguagesEnum> {
    return this.guest$
      .filter(g => g != null)
      .first()
      .map(g => g.lang)
      .toPromise();
  }

  public isComing(): boolean | undefined {
    const currentGuest = this._currentGuest;
    return currentGuest && currentGuest.coming as boolean;
  }

  public forceGuestChange(userId: string) {
    const currentGuest = this._currentGuest;
    if (currentGuest && currentGuest.id === userId) {
      this._guestSubject.next(currentGuest);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/guest.service.ts